import React, { useState, useEffect } from 'react';
import { saveConsent, loadConsent, CONSENT_COOKIE } from '../utils/cookies';

const defaultPolicyText = `Usamos cookies para mejorar su experiencia, ofrecer funcionalidades esenciales y analizar el uso del sitio. Puede aceptar todas las cookies, rechazarlas excepto las necesarias, o personalizar su elección.`;

const CookieConsent = ({ onChange }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setShowBanner(true);
    } else {
      setConsent(existing);
      setPrefs({
        necessary: existing.necessary ?? true,
        analytics: existing.analytics ?? false,
        marketing: existing.marketing ?? false,
      });
    }

    // Escuchar evento para abrir configuración desde footer
    const handleOpenSettings = () => {
      setManageOpen(true);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const applyConsent = (payload) => {
    saveConsent(payload);
    setConsent(payload);
    setShowBanner(false);
    setManageOpen(false);
    if (typeof onChange === 'function') onChange(payload);
  };

  const acceptAll = () => applyConsent({ necessary: true, analytics: true, marketing: true, timestamp: Date.now() });
  const rejectNonEssential = () => applyConsent({ necessary: true, analytics: false, marketing: false, timestamp: Date.now() });

  const saveManaged = () => applyConsent({ ...prefs, timestamp: Date.now() });

  if (!showBanner && !manageOpen) return null;

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:left-8 md:right-auto md:bottom-8 max-w-4xl mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-800">
              <div className="font-medium mb-1">Política de cookies</div>
              <div className="text-xs text-gray-600">
                {defaultPolicyText}
                <br />
                <a href="/politica-cookies" className="text-[#0b742f] hover:text-[#e87729] underline">
                  Ver política completa
                </a>
              </div>
            </div>

            <div className="flex items-center gap-2 justify-end">
              <button
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200"
                onClick={() => { setManageOpen(true); }}
              >
                Administrar
              </button>
              <button
                className="px-3 py-2 text-sm rounded-md bg-white border border-gray-200 text-gray-800 hover:bg-gray-50"
                onClick={rejectNonEssential}
              >
                Rechazar no esenciales
              </button>
              <button
                className="px-3 py-2 text-sm rounded-md bg-[#0b742f] text-white hover:brightness-90"
                onClick={acceptAll}
              >
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      )}

      {manageOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setManageOpen(false)} />
          <div className="relative w-full md:max-w-2xl bg-white rounded-t-lg md:rounded-lg p-6 m-4 md:m-0 shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Administrar cookies</h3>
            <p className="text-sm text-gray-600 mb-4">Seleccione las categorías que desea permitir.</p>

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">Necesarias</div>
                  <div className="text-xs text-gray-600">Cookies necesarias para el funcionamiento básico del sitio.</div>
                </div>
                <div>
                  <input type="checkbox" checked disabled className="w-4 h-4" />
                </div>
              </div>

              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">Analíticas</div>
                  <div className="text-xs text-gray-600">Nos ayudan a mejorar el rendimiento y entender el uso del sitio.</div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                    className="w-4 h-4"
                  />
                </div>
              </div>

              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">Marketing</div>
                  <div className="text-xs text-gray-600">Cookies usadas para personalización y publicidad.</div>
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                    className="w-4 h-4"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200"
                onClick={() => { setManageOpen(false); }}
              >
                Cancelar
              </button>
              <button
                className="px-3 py-2 text-sm rounded-md bg-white border border-gray-200 text-gray-800 hover:bg-gray-50"
                onClick={() => { saveConsent({ necessary: true, analytics: false, marketing: false, timestamp: Date.now() }); setManageOpen(false); setShowBanner(false); }}
              >
                Rechazar no esenciales
              </button>
              <button
                className="px-3 py-2 text-sm rounded-md bg-[#0b742f] text-white hover:brightness-95"
                onClick={saveManaged}
              >
                Guardar preferencias
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
